import apiClient from './apiClient';

// Flag to use mock data for development
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Mock notification data
const mockNotifications = [
  {
    id: 1,
    userId: 1, // admin
    type: 'system',
    title: 'System Update',
    message: 'The system will be updated on Saturday at 2:00 AM. Expect brief downtime.',
    read: false,
    createdAt: '2023-09-15T08:00:00.000Z'
  },
  {
    id: 2,
    userId: 2, // employee
    type: 'approval',
    title: 'Time Entry Approved',
    message: 'Your time entry for Acme Corporation on Sep 12 has been approved.',
    read: true,
    createdAt: '2023-09-14T15:30:00.000Z',
    relatedId: 101, // time entry ID
    relatedType: 'timeEntry'
  },
  {
    id: 3,
    userId: 2, // employee
    type: 'rejection',
    title: 'Time Entry Rejected',
    message: 'Your time entry for Globex Corporation on Sep 10 has been rejected. Please review and resubmit.',
    read: false,
    createdAt: '2023-09-13T11:20:00.000Z',
    relatedId: 102, // time entry ID
    relatedType: 'timeEntry'
  },
  {
    id: 4,
    userId: 3, // employee
    type: 'assignment',
    title: 'New Customer Assignment',
    message: 'You have been assigned to a new customer: Cyberdyne Systems.',
    read: false,
    createdAt: '2023-09-12T09:45:00.000Z',
    relatedId: 4, // customer ID
    relatedType: 'customer'
  },
  {
    id: 5,
    userId: 5, // customer
    type: 'timeEntry',
    title: 'New Time Entry',
    message: 'John Smith has submitted a new time entry for your review.',
    read: false,
    createdAt: '2023-09-15T10:15:00.000Z',
    relatedId: 103, // time entry ID
    relatedType: 'timeEntry'
  },
  {
    id: 6,
    userId: 2, // employee
    type: 'system',
    title: 'Profile Updated',
    message: 'Your profile information has been updated successfully.',
    read: true,
    createdAt: '2023-09-10T16:30:00.000Z'
  },
  {
    id: 7,
    userId: 1, // admin
    type: 'system',
    title: 'New User Registered',
    message: 'A new user has registered and requires approval: michael.johnson@example.com',
    read: false,
    createdAt: '2023-09-11T14:20:00.000Z',
    relatedId: 8, // user ID
    relatedType: 'user'
  },
  {
    id: 8,
    userId: 6, // customer
    type: 'report',
    title: 'Monthly Report Available',
    message: 'Your monthly time report for August is now available.',
    read: true,
    createdAt: '2023-09-05T08:00:00.000Z',
    relatedId: 25, // report ID
    relatedType: 'report'
  },
  {
    id: 9,
    userId: 3, // employee
    type: 'reminder',
    title: 'Time Entry Reminder',
    message: 'Please submit your time entries for this week by Friday 5:00 PM.',
    read: false,
    createdAt: '2023-09-14T09:00:00.000Z'
  },
  {
    id: 10,
    userId: 4, // employee
    type: 'approval',
    title: 'Time Entry Approved',
    message: 'Your time entry for Initech on Sep 11 has been approved.',
    read: false,
    createdAt: '2023-09-12T13:40:00.000Z',
    relatedId: 104, // time entry ID
    relatedType: 'timeEntry'
  }
];

// Mock notification preferences
const mockNotificationPreferences = {
  email: {
    timeEntryApproval: true,
    timeEntryRejection: true,
    newAssignment: true,
    systemUpdates: false,
    reminders: true
  },
  inApp: {
    timeEntryApproval: true,
    timeEntryRejection: true,
    newAssignment: true,
    systemUpdates: true,
    reminders: true
  }
};

/**
 * Get user notifications
 * @returns {Promise} - Promise containing notifications
 */
export const getNotifications = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Get current user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user')) || { id: 2 }; // Default to employee ID 2
      
      // Filter notifications for current user
      const userNotifications = mockNotifications.filter(notification => 
        notification.userId === user.id
      );
      
      // Sort by date (newest first)
      const sortedNotifications = userNotifications.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setTimeout(() => resolve({
        notifications: sortedNotifications,
        unreadCount: sortedNotifications.filter(n => !n.read).length
      }), 500);
    });
  }
  
  try {
    const response = await apiClient.get('/notifications');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Mark notification as read
 * @param {string} id - Notification ID
 * @returns {Promise} - Promise containing success message
 */
export const markAsRead = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Find notification
      const notification = mockNotifications.find(n => n.id === id);
      
      if (notification) {
        notification.read = true;
      }
      
      setTimeout(() => resolve({ success: true }), 300);
    });
  }
  
  try {
    const response = await apiClient.put(`/notifications/${id}/read`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise} - Promise containing success message
 */
export const markAllAsRead = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Get current user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user')) || { id: 2 }; // Default to employee ID 2
      
      // Mark all user notifications as read
      mockNotifications.forEach(notification => {
        if (notification.userId === user.id) {
          notification.read = true;
        }
      });
      
      setTimeout(() => resolve({ success: true }), 300);
    });
  }
  
  try {
    const response = await apiClient.put('/notifications/read-all');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete notification
 * @param {string} id - Notification ID
 * @returns {Promise} - Promise containing success message
 */
export const deleteNotification = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Find notification index
      const index = mockNotifications.findIndex(n => n.id === id);
      
      if (index !== -1) {
        mockNotifications.splice(index, 1);
      }
      
      setTimeout(() => resolve({ success: true }), 300);
    });
  }
  
  try {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get notification preferences
 * @returns {Promise} - Promise containing notification preferences
 */
export const getNotificationPreferences = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockNotificationPreferences), 300);
    });
  }
  
  try {
    const response = await apiClient.get('/notifications/preferences');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update notification preferences
 * @param {object} preferences - Updated notification preferences
 * @returns {Promise} - Promise containing updated preferences
 */
export const updateNotificationPreferences = async (preferences) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Update mock preferences
      Object.assign(mockNotificationPreferences, preferences);
      
      setTimeout(() => resolve(mockNotificationPreferences), 300);
    });
  }
  
  try {
    const response = await apiClient.put('/notifications/preferences', preferences);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

const notificationService = {
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getNotificationPreferences,
  updateNotificationPreferences
};

export default notificationService;